import { remiApi } from "../api/remiApi";
import { User, TeamMember } from "../types/user";
import { logger as Logger } from "../utils/logger";
import { teamMemberService } from "../api/teamMembers";
import { fetchIdxConsentProfile } from "./idxConsentProfile";
import {
  resolveIdxConsentReceipts,
  type IdxConsentReceipt,
} from "../config/idxConsentReceipts";

const DEFAULT_VIEW_LIMIT = 2; // Default number of property views before requiring registration

export interface ContactAddress {
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
}

// Auto-fill contact shape returned by findContactById. Kept camelCase so the
// existing auto-fill hooks and web-activity guest data keep reading the same
// fields after the swap off Firestore.
export interface ContactData {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  organization_id: string;
  address?: ContactAddress | null;
}

// GET /api/v1/idx/contacts/{id}/profile
interface IdxContactProfileResponse {
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone_number: string | null;
  address: ContactAddress | null;
}

// POST /api/v1/contacts/idx-signup
interface IdxSignupResponse {
  success: boolean;
  contact_id: string;
  web_user_id: string;
  created_new_contact: boolean;
  used_fallback_contact_id: boolean;
}

// POST /api/v1/contacts/idx-validate
interface IdxAssignedAgent {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone_number: string | null;
  photo_url: string | null;
  slug: string | null;
}
interface IdxValidateUser {
  web_user_id: string;
  contact_id: string;
  organization_id: string;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
  phone_number: string | null;
  assigned_agent_id: string | null;
  assigned_agent: IdxAssignedAgent | null;
}
interface IdxValidateResponse {
  valid: boolean;
  reason?: string;
  user?: IdxValidateUser;
}

// The `?agent=`-referral agent the backend assigned to this contact. Mapped into
// the User's `claimedLead` (the "Your Dedicated Agent" card reads that field).
const toClaimedLead = (
  agent: IdxAssignedAgent | null,
  organizationId: string
): TeamMember | undefined => {
  if (!agent?.id) return undefined;
  return {
    id: agent.id,
    organization_id: organizationId,
    first_name: agent.first_name ?? "",
    last_name: agent.last_name ?? "",
    email: agent.email ?? undefined,
    phone_number: agent.phone_number ?? undefined,
    photo_url: agent.photo_url ?? undefined,
    slug: agent.slug ?? undefined,
  };
};

const DEFAULT_SETTINGS: User["settings"] = {
  notifications: false,
  preferences: { defaultView: "list", theme: "light" },
};

const defaultPropertyAccess = (): User["propertyAccess"] => ({
  savedProperties: [],
  recentSearches: [],
  viewCount: 0,
  lastReset: new Date().toISOString(),
  viewLimit: DEFAULT_VIEW_LIMIT,
});

// The IDX contact profile lives in Spanner, not on the client, so the local
// settings/propertyAccess fields (never persisted server-side) are hydrated
// with defaults. They exist only for view-gating UI state.
const buildUser = (partial: {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  organizationId: string;
  firebaseUid?: string;
  claimedLead?: TeamMember;
}): User => ({
  id: partial.id,
  email: partial.email,
  firstName: partial.firstName,
  lastName: partial.lastName,
  phoneNumber: partial.phoneNumber,
  role: "user",
  organization_id: partial.organizationId,
  firebaseUid: partial.firebaseUid,
  settings: DEFAULT_SETTINGS,
  propertyAccess: defaultPropertyAccess(),
  claimedLead: partial.claimedLead,
});

export const userService = {
  /**
   * §7 — Auto-fill profile for `?contact_id=` marketing-email landings. Public
   * (org-scoped) Spanner read; 404 means no matching contact in this org (the
   * endpoint never distinguishes wrong-org from missing), treated as not-found.
   */
  async findContactById(
    contactId: string,
    organizationId: string
  ): Promise<ContactData | null> {
    try {
      const { data } = await remiApi.get<IdxContactProfileResponse>(
        `/idx/contacts/${encodeURIComponent(contactId)}/profile`,
        { params: { org_id: organizationId } }
      );
      return {
        id: contactId,
        email: data.email ?? "",
        firstName: data.first_name ?? undefined,
        lastName: data.last_name ?? undefined,
        phoneNumber: data.phone_number ?? undefined,
        organization_id: organizationId,
        address: data.address ?? null,
      };
    } catch (error) {
      Logger.error("Error finding contact by ID", {
        contactId,
        organizationId,
        error,
      });
      return null;
    }
  },

  /**
   * §2 — IDX signup. The backend bootstraps the Contact + WebUser and runs the
   * full `webUserCreated` enrichment (lead source/intent, opt-in, tags, valuation
   * record, convert-flow enroll, eAlerts) server-side on Spanner, replacing the
   * old client Firestore transaction. Must be called after Firebase sign-in so
   * the request carries a matching ID token (the endpoint verifies uid + email).
   */
  async getOrCreateUserProfile(firebaseUser: {
    id: string;
    email: string;
    firstName?: string;
    lastName?: string;
    phoneNumber?: string;
    marketingConsent?: boolean;
    consentReceipts?: IdxConsentReceipt[];
    gaClientId?: string;
    agent?: string;
    tenantId?: string;
  }): Promise<User> {
    if (!firebaseUser.tenantId) {
      throw new Error(
        "Cannot create or link contact without organization ID (tenantId is missing)"
      );
    }

    // A visitor arrives via an agent referral link (`?agent=<email>`); on signup
    // that agent is assigned to the lead. Resolve the email to a team member so
    // we can send its id as `assigned_agent_id` (the backend persists the
    // assignment from that id). The resolved object is kept locally for the
    // returned User (dashboard agent card).
    let assignedAgent: TeamMember | undefined;
    if (firebaseUser.agent) {
      const teamMember = await teamMemberService.getTeamMemberByEmail(
        firebaseUser.tenantId,
        firebaseUser.agent
      );
      if (teamMember) {
        assignedAgent = teamMember;
      }
    }

    // The server decides whether this org is in the FUB-first capture scope.
    // If that lookup fails, retry with the captured receipts instead of
    // successfully creating a profile without the visitor's choices.
    const consentReceipts = await resolveIdxConsentReceipts(
      firebaseUser.consentReceipts,
      firebaseUser.tenantId,
      fetchIdxConsentProfile
    );

    const { data } = await remiApi.post<IdxSignupResponse>(
      "/contacts/idx-signup",
      {
        organization_id: firebaseUser.tenantId,
        email: firebaseUser.email,
        firebase_uid: firebaseUser.id,
        first_name: firebaseUser.firstName,
        last_name: firebaseUser.lastName,
        phone_number: firebaseUser.phoneNumber,
        sign_in_provider:
          typeof window !== "undefined" ? window.location.href : undefined,
        marketing_consent: firebaseUser.marketingConsent,
        ...(consentReceipts ? { consent_receipts: consentReceipts } : {}),
        ga_client_id: firebaseUser.gaClientId,
        assigned_agent_id: assignedAgent?.id,
      }
    );

    return buildUser({
      id: data.contact_id,
      email: firebaseUser.email,
      firstName: firebaseUser.firstName,
      lastName: firebaseUser.lastName,
      phoneNumber: firebaseUser.phoneNumber,
      organizationId: firebaseUser.tenantId,
      firebaseUid: firebaseUser.id,
      claimedLead: assignedAgent,
    });
  },

  /**
   * §3 — Validate a signed-in IDX web visitor against Spanner (replaces the
   * email-keyed Firestore Contacts query). Returns the resolved `User` when the
   * Firebase uid maps to an active WebUser, else `{ valid: false }`. The response
   * carries the assigned agent, so the dashboard agent card populates on login.
   */
  async validateWebUser(
    firebaseUid: string,
    organizationId: string
  ): Promise<{ valid: boolean; reason?: string; user: User | null }> {
    const { data } = await remiApi.post<IdxValidateResponse>(
      "/contacts/idx-validate",
      { organization_id: organizationId }
    );
    if (!data.valid || !data.user) {
      return { valid: false, reason: data.reason, user: null };
    }
    const u = data.user;
    return {
      valid: true,
      user: buildUser({
        id: u.contact_id,
        email: u.email ?? "",
        firstName: u.first_name ?? undefined,
        lastName: u.last_name ?? undefined,
        phoneNumber: u.phone_number ?? undefined,
        organizationId: u.organization_id,
        firebaseUid,
        claimedLead: toClaimedLead(u.assigned_agent, u.organization_id),
      }),
    };
  },

  /**
   * Logged-in Canadian valuation request. The Firestore
   * `Contacts.valuationRequested` array has no Spanner endpoint; the request is
   * already recorded via web-activity tracking (`trackValuationRequest`) at the
   * call site, so this is a logged no-op until a dedicated endpoint exists.
   */
  async appendValuationRequest(
    userId: string,
    requestedAddress: string
  ): Promise<void> {
    Logger.debug(
      "Valuation request captured via web activity; no Spanner append endpoint yet",
      { userId, requestedAddress }
    );
  },
};
